import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAddressBook, IconMapPins } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, FormItemValueType, type EnsoFormItem } from '@ohpen/enso-ui';
import { Checkbox, Form } from 'antd';
import { Address, lenderFirmsGetTradingAddresses, TradingAddressResponse } from '@pmp/api';

import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField, formatAddressAsLines } from '../../firms';

export const ContactDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const [tradingAddresses, setTradingAddresses] = useState<TradingAddressResponse[]>([]);

  const watchedFirmId = Form.useWatch('firmId', form);
  const watchedTradingAddressId = Form.useWatch('tradingAddressId', form);
  const watchedTradingAddress = Form.useWatch('tradingAddress', form);

  useEffect(() => {
    if (!watchedFirmId) {
      setTradingAddresses([]);
      return;
    }
    let cancelled = false;
    lenderFirmsGetTradingAddresses({ path: { firmId: watchedFirmId } })
      .then(({ data }) => {
        if (!cancelled) setTradingAddresses(data?.filter(a => a.isActive !== false) ?? []);
      })
      .catch(() => { if (!cancelled) setTradingAddresses([]); });
    return () => { cancelled = true; };
  }, [watchedFirmId]);

  const tradingAddressOptions = useMemo(
    () =>
      tradingAddresses
        .filter(a => a.id)
        .map(a => ({
          label: a.name || formatAddressAsLines(a as Address),
          value: a.id!,
        })),
    [tradingAddresses],
  );

  const selectedAddress = useMemo(
    () => tradingAddresses.find(a => a.id === watchedTradingAddressId),
    [tradingAddresses, watchedTradingAddressId],
  );

  const getSelectedAddress = useCallback(
    (): Address => (selectedAddress ?? watchedTradingAddress ?? {}) as Address,
    [selectedAddress, watchedTradingAddress],
  );

  const contactColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.mobile'),
            dataIndex: ['personalData', 'mobilePhoneNumber'],
            valueType: FormItemValueType.text,
            readonly,
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.telephone'),
            dataIndex: ['personalData', 'workPhoneNumber'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.brokers.labels.email'),
        dataIndex: 'email',
        valueType: FormItemValueType.text,
        readonly,
        formItemProps: {
          rules: [{ required: true, type: 'email' }],
        },
        colProps: { span: 12 },
      },
      {
        dataIndex: 'receiveMarketingMaterials',
        valueType: 'switch' as const,
        readonly,
        renderFormItem: (_, { value }, formInstance) => (
          <Checkbox
            checked={!!value}
            onChange={e => formInstance?.setFieldValue('receiveMarketingMaterials', e.target.checked)}
          >
            {t('lender.brokers.labels.marketingMaterials')}
          </Checkbox>
        ),
        renderReadonlyFormItem: (_, { value }) => (
          <Checkbox checked={!!value} disabled>
            {t('lender.brokers.labels.marketingMaterials')}
          </Checkbox>
        ),
        colProps: { span: 24 },
      },
      {
        dataIndex: 'agreedToTermsAndConditions',
        valueType: 'switch' as const,
        readonly,
        renderFormItem: (_, { value }, formInstance) => (
          <Checkbox
            checked={!!value}
            onChange={e => formInstance?.setFieldValue('agreedToTermsAndConditions', e.target.checked)}
          >
            {t('lender.brokers.labels.termsAndConditions')}
          </Checkbox>
        ),
        renderReadonlyFormItem: (_, { value }) => (
          <Checkbox checked={!!value} disabled>
            {t('lender.brokers.labels.termsAndConditions')}
          </Checkbox>
        ),
        colProps: { span: 24 },
      },
    ],
    [readonly, t],
  );

  const tradingAddressColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.tradingAddress'),
        dataIndex: 'tradingAddressId',
        valueType: FormItemValueType.select,
        readonly,
        fieldProps: {
          options: tradingAddressOptions,
          showSearch: true,
          optionFilterProp: 'label',
        },
        colProps: { span: 12 },
      },
    ],
    [readonly, t, tradingAddressOptions],
  );

  return (
    <>
      <CrudFormCard icon={<IconAddressBook />} title={t('lender.brokers.labels.contactDetails')}>
        <CrudSchemaFields schema={contactColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconMapPins />} title={t('lender.brokers.labels.tradingAddress')}>
        <CrudSchemaFields schema={tradingAddressColumns} form={form} readonly={readonly} component={false} />
        {(selectedAddress || watchedTradingAddress) && (
          <AddressField
            key={watchedTradingAddressId}
            readonly
            form={form}
            getAddress={getSelectedAddress}
          />
        )}
      </CrudFormCard>
    </>
  );
};
